import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, arrayUnion, arrayRemove, where, query, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { Event, EVENT_ZONES } from '../types/Event';
import { useAuth } from '../contexts/AuthContext';
import { MEMBER_TYPES } from '../types/User';
import { useEventBackgrounds } from '../pages/events/useEventBackgrounds';

export const useEvents = () => {
  const { user } = useAuth();
  const [pastEvents, setPastEvents] = useState<Event[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Utiliser le hook partagé pour les images de fond
  // On combine tous les événements pour que le hook puisse gérer le cache globalement
  const allEvents = useMemo(() => [...pastEvents, ...upcomingEvents], [pastEvents, upcomingEvents]);
  const eventBackgrounds = useEventBackgrounds(allEvents);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const today = new Date();
        const eventsRef = collection(db, 'events');
        const querySnapshot = await getDocs(eventsRef);
        const eventsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Event[];

        const filteredEvents = eventsData.filter(event => {
          if (event.zone === EVENT_ZONES.HOME) {
            return user && user.verified && (user.role === MEMBER_TYPES.GOLD || user.role === MEMBER_TYPES.ADMIN);
          }
          if (event.zone === EVENT_ZONES.WAN) {
            return user && (user.role === MEMBER_TYPES.ADMIN || user.role === MEMBER_TYPES.GOLD || user.role === MEMBER_TYPES.SILVER);
          }
          if (event.zone === EVENT_ZONES.COMMUNAL) {
            return true;
          }
          return false;
        });

        // Helper pour convertir date Firestore/String en Date JS pour le tri
        const getValidDate = (dateIdx: any): number => {
          if (!dateIdx) return 0;
          if (typeof dateIdx === 'object' && dateIdx.seconds) return dateIdx.seconds * 1000;
          const d = new Date(dateIdx);
          return isNaN(d.getTime()) ? 0 : d.getTime();
        };

        const sortedFilteredEvents = filteredEvents.sort((a, b) => getValidDate(a.startDate) - getValidDate(b.startDate));

        const past: Event[] = [];
        const upcoming: Event[] = [];

        for (const event of sortedFilteredEvents) {
          // On utilise la date de fin pour savoir si c'est passé
          const endDateTimestamp = getValidDate(event.endDate);
          const todayTimestamp = today.getTime();

          if (endDateTimestamp < todayTimestamp) {
            past.push(event);
          } else {
            upcoming.push(event);
          }
        }

        // Tri final : 
        // Passé : du plus récent au plus vieux
        past.sort((a, b) => getValidDate(b.startDate) - getValidDate(a.startDate)); // DESC
        // Futur : du plus proche au plus lointain
        upcoming.sort((a, b) => getValidDate(a.startDate) - getValidDate(b.startDate)); // ASC

        const limitedPastEvents = past.slice(0, 100);
        setPastEvents(limitedPastEvents);
        setUpcomingEvents(upcoming);
      } catch (error) {
        console.error('Erreur lors de la récupération des événements:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [user]);

  const handleRegisterEvent = async (idsToAdd?: string[], idsToRemove?: string[]) => {
    if (!selectedEvent || !selectedEvent.id || !user) {
      setOpenDialog(false);
      return;
    }

    try {
      const eventRef = doc(db, 'events', selectedEvent.id);
      const currentParticipants = selectedEvent.participants || [];

      // Mode gestion de famille
      if (idsToAdd || idsToRemove) {
        const toAdd = idsToAdd || [];
        const toRemove = idsToRemove || [];

        // Vérification places (sommaire, pour l'UX immédiate)
        if (toAdd.length > 0) {
          const newRegistrationsCount = toAdd.filter(id => !currentParticipants.includes(id)).length;
          const spotsFreed = toRemove.filter(id => currentParticipants.includes(id)).length;

          if (currentParticipants.length - spotsFreed + newRegistrationsCount > selectedEvent.maxParticipants) {
            alert("Pas assez de places disponibles.");
            return;
          }
        }

        if (toRemove.length > 0) {
          await updateDoc(eventRef, {
            participants: arrayRemove(...toRemove)
          });
        }

        if (toAdd.length > 0) {
          await updateDoc(eventRef, {
            participants: arrayUnion(...toAdd)
          });
        }

        // Mise à jour locale optimiste
        let updatedParticipants = [...currentParticipants];
        // Retrait
        updatedParticipants = updatedParticipants.filter(id => !toRemove.includes(id));
        // Ajout (sans doublon)
        toAdd.forEach(id => {
          if (!updatedParticipants.includes(id)) updatedParticipants.push(id);
        });

        const updatedEvents = upcomingEvents.map(event => {
          if (event.id === selectedEvent.id) {
            return { ...event, participants: updatedParticipants };
          }
          return event;
        });

        setUpcomingEvents(updatedEvents);
        setSelectedEvent({ ...selectedEvent, participants: updatedParticipants });

      } else {
        // Legacy Mode (Toggle User)
        const isRegistered = currentParticipants.includes(user.uid);

        if (isRegistered) {
          await updateDoc(eventRef, {
            participants: arrayRemove(user.uid)
          });
        } else {
          const isFull = currentParticipants.length >= selectedEvent.maxParticipants;
          if (isFull) {
            alert("Cet événement est complet. Vous ne pouvez pas vous y inscrire.");
            setOpenDialog(false);
            return;
          }

          await updateDoc(eventRef, {
            participants: arrayUnion(user.uid)
          });
        }

        // Mise à jour locale
        const updatedParticipants = isRegistered
          ? currentParticipants.filter(id => id !== user.uid)
          : [...currentParticipants, user.uid];

        const updatedEvents = upcomingEvents.map(event => {
          if (event.id === selectedEvent.id) {
            return { ...event, participants: updatedParticipants };
          }
          return event;
        });

        setUpcomingEvents(updatedEvents);
        setSelectedEvent({ ...selectedEvent, participants: updatedParticipants });
      }

    } catch (error) {
      console.error("Erreur lors de l'inscription/désinscription:", error);
    } finally {
      setOpenDialog(false);
    }
  };

  return {
    pastEvents,
    upcomingEvents,
    loading,
    eventBackgrounds,
    selectedEvent,
    setSelectedEvent,
    openDialog,
    setOpenDialog,
    handleRegisterEvent
  };
};
